import React from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faCircleCheck,
  faCommentDots,
  faPhone,
  faStore,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

const fieldRow = (label, value) => {
  if (value === undefined || value === null || value === "" || value === "-") {
    return null;
  }

  return (
    <div className="crm-alert-accordion-detail-row" key={`${label}-${value}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
};

export default function AlertAccordionSection({
  darkMode = false,
  title,
  total = 0,
  rows = [],
  emptyMessage,
  searchValue,
  onSearchChange,
  placeholder,
  collapsedSet,
  onToggleCollapse,
  onActionClick,
  onCopyMessage,
  currency,
  percent,
  PostventaBadge,
  toneForPriority,
  toneForGestionState,
  toneForSegment,
  toneForPattern,
}) {
  return (
    <section className={`crm-postventa-panel crm-alert-surface ${darkMode ? "is-dark" : ""}`}>
      <div className="crm-alert-section-header">
        <div>
          <h3>{title}</h3>
          <span className="crm-postventa-card-label">Mostrando {rows.length} de {total}</span>
        </div>
        <div className="crm-alert-section-search">
          <Form.Control
            type="search"
            className="crm-postventa-filter-control"
            placeholder={placeholder}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            aria-label={`Buscar en ${title}`}
          />
        </div>
      </div>

      <div className="crm-alert-accordion-list">
        {rows.length ? rows.map((item, index) => {
          const cardId = `${title}-${item.cuit}-${item.terminal}-${item.tipoAlerta}-${index}`;
          const collapsed = collapsedSet.has(cardId);
          const hasFacturacionMetrics = item.motivoCodigo === "FALTA_FACTURACION" || item.motivoCodigo === "CAIDA_FACTURACION";
          const detailRows = [
            fieldRow("Motivo principal", item.tipoAlerta),
            fieldRow("Acción sugerida", item.accionSugerida),
            fieldRow("Patrón detectado", item.patronFacturacion),
            fieldRow("Último contacto", item.ultimaGestion),
            fieldRow("Próximo contacto", item.proximoContacto),
            fieldRow("Responsable", item.responsable),
            fieldRow("Provincia", item.provincia),
            fieldRow("Teléfono", item.telefono),
          ].filter(Boolean);

          return (
            <article className={`crm-alert-accordion-card ${darkMode ? "is-dark" : ""}`} key={cardId}>
              <button
                type="button"
                className="crm-alert-accordion-head"
                onClick={() => onToggleCollapse(cardId)}
                aria-expanded={!collapsed}
              >
                <div className="crm-alert-accordion-head-main">
                  <div className="crm-alert-accordion-title-line">
                    <strong>{item.comercio || "Sin comercio"}</strong>
                    <span>CUIT {item.cuit || "-"}</span>
                    <span>Terminal {item.terminal || "-"}</span>
                  </div>
                  <div className="crm-alert-accordion-meta-line">
                    <span>{item.provincia || "Sin provincia"}</span>
                    <span>{item.telefono || "Sin teléfono"}</span>
                    <span>{item.responsable || "Sin responsable"}</span>
                  </div>
                </div>

                <div className="crm-alert-accordion-head-badges">
                  <PostventaBadge value={item.estado || "Pendiente"} tone={toneForGestionState(item.estado)} />
                  <PostventaBadge value={`Score ${item.scorePrioridad || 0}`} tone={toneForPriority(item.prioridad)} />
                  <PostventaBadge value={item.prioridad || "Media"} tone={toneForPriority(item.prioridad)} />
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="crm-postventa-action-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onActionClick(item);
                    }}
                  >
                    Acciones
                  </Button>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="crm-alert-accordion-chevron"
                    style={{ transform: collapsed ? "none" : "rotate(180deg)" }}
                  />
                </div>
              </button>

              {!collapsed ? (
                <div className="crm-alert-accordion-body">
                  <div className="crm-alert-accordion-pill-row">
                    <PostventaBadge value={`Segmento ${item.segmentoComercial || "Nuevo"}`} tone={toneForSegment(item.segmentoComercial)} />
                    <PostventaBadge value={item.patronFacturacion || "NUEVO"} tone={toneForPattern(item.patronFacturacion)} />
                    {item.tieneSeguimientoActivo ? <PostventaBadge value="Seguimiento activo" tone="success" /> : null}
                  </div>

                  <div className="crm-alert-accordion-grid">
                    <div className="crm-alert-accordion-panel">
                      <div className="crm-alert-info-title">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        <span>Detalle</span>
                      </div>
                      <div className="crm-alert-accordion-detail-list">
                        {detailRows}
                      </div>
                    </div>

                    {hasFacturacionMetrics ? (
                      <div className="crm-alert-accordion-panel">
                        <div className="crm-alert-info-title">
                          <FontAwesomeIcon icon={faStore} />
                          <span>Facturación</span>
                        </div>
                        <div className="crm-alert-metric-stack">
                          <div><span>Hoy</span><strong>{currency(item.montoHoy)}</strong></div>
                          <div><span>Promedio 7 días</span><strong>{currency(item.promedio7Dias)}</strong></div>
                          <div><span>Variación</span><strong>{percent(item.variacion)}</strong></div>
                          <div><span>Facturación 30 días</span><strong>{currency(item.facturacion30Dias)}</strong></div>
                        </div>
                      </div>
                    ) : (
                      <div className="crm-alert-accordion-panel">
                        <div className="crm-alert-info-title">
                          <FontAwesomeIcon icon={faCircleCheck} />
                          <span>Contexto</span>
                        </div>
                        <p className="crm-alert-support-copy mb-0">{item.resumen || item.motivoPrioridad || "Sin contexto adicional."}</p>
                      </div>
                    )}

                    <div className="crm-alert-accordion-panel">
                      <div className="crm-alert-info-title">
                        <FontAwesomeIcon icon={faCommentDots} />
                        <span>Mensaje sugerido</span>
                      </div>
                      <p className="crm-alert-message-quote">
                        {item.mensajeSugerido && item.mensajeSugerido !== "-" ? item.mensajeSugerido : "Sin mensaje sugerido disponible."}
                      </p>
                      {item.mensajeSugerido && item.mensajeSugerido !== "-" ? (
                        <div className="crm-alert-inline-actions">
                          <Button size="sm" variant="outline-secondary" onClick={() => onCopyMessage(item.mensajeSugerido)}>
                            Copiar mensaje
                          </Button>
                        </div>
                      ) : null}
                      <div className="crm-alert-accordion-detail-list">
                        {fieldRow("Resultado", item.estado)}
                        {fieldRow("Última actividad", item.ultimaActividad)}
                        {fieldRow("Último Zoco Check", item.ultimoZocoCheck)}
                      </div>
                    </div>
                  </div>

                  <div className="crm-alert-footer-note">
                    <FontAwesomeIcon icon={faPhone} />
                    <span>{item.telefono || "Sin teléfono"} · {item.campania || "Sin campaña"}</span>
                  </div>
                </div>
              ) : null}
            </article>
          );
        }) : (
          <Alert variant="secondary" className="mb-0">{emptyMessage}</Alert>
        )}
      </div>
    </section>
  );
}
